import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Switch,
} from '@mui/material';
import { useSelector } from 'react-redux';
import { User, UserFormData, RoleEnum } from '../../../types';
import { RootState } from '@/store';
import { usersService } from '../../../services';
import { useNotification } from '../../../contexts/NotificationContext';
import { canPerformAction, ActionKey } from '@/auth/actionMatrix';

interface UserFormProps {
  open: boolean;
  editingUser: User | null;
  formData: UserFormData;
  error: string | null;
  onClose: () => void;
  onSubmit: () => void;
  onChange: (data: UserFormData) => void;
}

const UserForm: React.FC<UserFormProps> = ({
  open,
  editingUser,
  formData,
  error,
  onClose,
  onSubmit,
  onChange,
}) => {
  const currentUser = useSelector((state: RootState) => state.auth.user);
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const [passwordData, setPasswordData] = useState({
    new_password: '',
    confirm_password: '',
  });
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const { showNotification } = useNotification();
  const canToggleSuperuser = canPerformAction(currentUser, ActionKey.USERS_TOGGLE_SUPERUSER);
  const isProductManager = currentUser?.role === RoleEnum.PRODUCT_MANAGER && !currentUser?.is_superuser;
  const roleLabels: Record<string, string> = {
    [RoleEnum.ADMIN]: 'Администратор',
    [RoleEnum.STAFF]: 'Сотрудник',
    [RoleEnum.CLIENT_MANAGER]: 'Клиент-менеджер',
    [RoleEnum.SUPPORT]: 'Саппорт',
    [RoleEnum.PRODUCT_MANAGER]: 'Продукт-менеджер',
    [RoleEnum.TRADER]: 'Трейдер',
    [RoleEnum.TRADERS_OWNER]: 'Владелец трейдеров',
    [RoleEnum.MERCHANT]: 'Мерчант',
    [RoleEnum.GUEST]: 'Гость',
  };
  const availableRoleOptions = isProductManager
    ? [{
        value: editingUser ? formData.role : RoleEnum.TRADER,
        label: editingUser ? (roleLabels[formData.role] || formData.role) : 'Трейдер',
      }]
    : [
        { value: RoleEnum.ADMIN, label: 'Администратор' },
        { value: RoleEnum.CLIENT_MANAGER, label: 'Клиент-менеджер' },
        { value: RoleEnum.SUPPORT, label: 'Саппорт' },
        { value: RoleEnum.TRADER, label: 'Трейдер' },
        { value: RoleEnum.TRADERS_OWNER, label: 'Владелец трейдеров' },
        { value: RoleEnum.MERCHANT, label: 'Мерчант' },
        { value: RoleEnum.GUEST, label: 'Гость' },
      ];

  const handleChange = (field: keyof UserFormData, value: any) => {
    onChange({ ...formData, [field]: value });
  };

  const handlePasswordChange = (field: keyof typeof passwordData) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setPasswordData(prev => ({ ...prev, [field]: e.target.value }));
  };

  const handleChangePassword = async () => {
    if (!editingUser) return;
    
    if (passwordData.new_password !== passwordData.confirm_password) {
      setPasswordError('Пароли не совпадают');
      return;
    }

    if (passwordData.new_password.length < 8) {
      setPasswordError('Пароль должен содержать минимум 8 символов');
      return;
    }

    try {
      await usersService.changePassword(editingUser.id, passwordData);
      showNotification('Пароль успешно изменен', 'success');
      setChangePasswordOpen(false);
      setPasswordData({ new_password: '', confirm_password: '' });
      setPasswordError(null);
    } catch (err: any) {
      setPasswordError(err.response?.data?.detail || 'Ошибка при смене пароля');
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose}>
        <DialogTitle>
          {editingUser ? 'Редактировать пользователя' : 'Новый пользователь'}
        </DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <TextField
            margin="dense"
            label="Логин"
            type="text"
            fullWidth
            value={formData.username}
            onChange={(e) => handleChange('username', e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Email"
            type="email"
            fullWidth
            value={formData.email}
            onChange={(e) => handleChange('email', e.target.value)}
            sx={{ mb: 2 }}
          />
          {!editingUser && (
            <TextField
              margin="dense"
              label="Пароль"
              type="password"
              fullWidth
              value={formData.password}
              onChange={(e) => handleChange('password', e.target.value)}
              helperText="Минимум 8 символов"
              sx={{ mb: 2 }}
            />
          )}
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Роль</InputLabel>
            <Select
              value={formData.role}
              label="Роль"
              onChange={(e) => handleChange('role', e.target.value)}
              disabled={Boolean(editingUser && isProductManager)}
            >
              {availableRoleOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Box sx={{ mt: 2 }}>
            <Typography component="label" display="block">
              <Switch
                checked={formData.is_active}
                onChange={(e) => handleChange('is_active', e.target.checked)}
              />
              Активен
            </Typography>
          </Box>
          <Box sx={{ mt: 1 }}>
            <Typography component="label" display="block">
              <Switch
                checked={formData.is_superuser}
                onChange={(e) => handleChange('is_superuser', e.target.checked)}
                disabled={!canToggleSuperuser}
              />
              Администратор
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          {editingUser && (
            <Button onClick={() => setChangePasswordOpen(true)}>
              Изменить пароль
            </Button>
          )}
          <Button onClick={onClose}>Отмена</Button>
          <Button onClick={onSubmit} variant="contained">
            {editingUser ? 'Сохранить' : 'Создать'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={changePasswordOpen} onClose={() => setChangePasswordOpen(false)}>
        <DialogTitle>Изменить пароль</DialogTitle>
        <DialogContent>
          {passwordError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {passwordError}
            </Alert>
          )}
          <TextField
            margin="dense"
            label="Новый пароль"
            type="password"
            fullWidth
            value={passwordData.new_password}
            onChange={handlePasswordChange('new_password')}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Подтвердите пароль"
            type="password"
            fullWidth
            value={passwordData.confirm_password}
            onChange={handlePasswordChange('confirm_password')}
            sx={{ mb: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setChangePasswordOpen(false)}>Отмена</Button>
          <Button onClick={handleChangePassword} variant="contained">
            Подтвердить
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default UserForm; 
